/**
 * SkeletonLoader — animated placeholder shimmer for loading states.
 * Renders configurable rows that pulse to indicate content is loading.
 */

import React, { useEffect, useRef, useState } from 'react';
import { View, Animated, StyleSheet, AccessibilityInfo, type DimensionValue } from 'react-native';
import { SPACING, RADIUS } from '../../utils/design-tokens';
import { useTheme } from '../../utils/theme';

interface SkeletonRowConfig {
  width: DimensionValue;
  height: number;
  borderRadius?: number;
  marginBottom?: number;
}

interface Props {
  /** Preset layout: 'reminder-list' | 'chat' | 'calendar' | 'card' */
  variant?: 'reminder-list' | 'chat' | 'calendar' | 'card';
  /** Number of skeleton items to render */
  rows?: number;
  /** Custom row configs (overrides variant) */
  customRows?: SkeletonRowConfig[];
}

function SkeletonBar({ width, height, borderRadius = 8, opacity }: SkeletonRowConfig & { opacity: Animated.Value }) {
  const { colors } = useTheme();
  return (
    <Animated.View
      style={[
        styles.bar,
        {
          width,
          height,
          borderRadius,
          backgroundColor: colors.skeleton,
          opacity,
        },
      ]}
    />
  );
}

function ReminderSkeleton({ opacity }: { opacity: Animated.Value }) {
  const { colors } = useTheme();
  return (
    <View style={[styles.reminderCard, { backgroundColor: colors.surface, borderColor: colors.border }]}>
      <View style={[styles.dot, { backgroundColor: colors.skeleton }]} />
      <View style={styles.reminderContent}>
        <SkeletonBar width="75%" height={14} opacity={opacity} />
        <View style={styles.reminderMeta}>
          <SkeletonBar width={80} height={10} opacity={opacity} />
          <SkeletonBar width={50} height={10} opacity={opacity} />
        </View>
      </View>
      <View style={[styles.actionCircle, { backgroundColor: colors.skeleton }]} />
    </View>
  );
}

function ChatSkeleton({ opacity, isUser }: { opacity: Animated.Value; isUser: boolean }) {
  const { colors } = useTheme();
  return (
    <View style={[styles.chatRow, isUser && styles.chatRowUser]}>
      <View style={[styles.chatBubble, { backgroundColor: colors.skeleton }, isUser && styles.chatBubbleUser]}>
        <SkeletonBar width={isUser ? 120 : 180} height={12} opacity={opacity} />
        {!isUser && <SkeletonBar width={140} height={12} opacity={opacity} />}
      </View>
    </View>
  );
}

function CalendarSkeleton({ opacity }: { opacity: Animated.Value }) {
  const { colors } = useTheme();
  return (
    <View style={[styles.calendarItem, { borderLeftColor: colors.skeleton }]}>
      <SkeletonBar width={50} height={10} opacity={opacity} />
      <SkeletonBar width="80%" height={14} opacity={opacity} />
      <SkeletonBar width={70} height={10} opacity={opacity} />
    </View>
  );
}

export default function SkeletonLoader({ variant = 'reminder-list', rows = 5, customRows }: Props) {
  const pulseAnim = useRef(new Animated.Value(0.4)).current;
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    AccessibilityInfo.isReduceMotionEnabled().then(setReduceMotion);
  }, []);

  useEffect(() => {
    if (reduceMotion) {
      pulseAnim.setValue(0.7);
      return;
    }
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, { toValue: 1, duration: 800, useNativeDriver: true }),
        Animated.timing(pulseAnim, { toValue: 0.4, duration: 800, useNativeDriver: true }),
      ])
    );
    loop.start();
    return () => loop.stop();
  }, [pulseAnim]);

  if (customRows) {
    return (
      <View style={styles.container}>
        {customRows.map((row, i) => (
          <View key={i} style={{ marginBottom: row.marginBottom ?? 12 }}>
            <SkeletonBar {...row} opacity={pulseAnim} />
          </View>
        ))}
      </View>
    );
  }

  if (variant === 'reminder-list') {
    return (
      <View style={styles.container}>
        {/* Section header skeleton */}
        <View style={styles.sectionHeader}>
          <SkeletonBar width={120} height={12} opacity={pulseAnim} />
        </View>
        {Array.from({ length: rows }).map((_, i) => (
          <ReminderSkeleton key={i} opacity={pulseAnim} />
        ))}
      </View>
    );
  }

  if (variant === 'chat') {
    const pattern = [false, false, true, false, true, false, false, true];
    return (
      <View style={styles.container}>
        {Array.from({ length: rows }).map((_, i) => (
          <ChatSkeleton key={i} opacity={pulseAnim} isUser={pattern[i % pattern.length]} />
        ))}
      </View>
    );
  }

  if (variant === 'calendar') {
    return (
      <View style={styles.container}>
        <View style={styles.sectionHeader}>
          <SkeletonBar width={100} height={12} opacity={pulseAnim} />
        </View>
        {Array.from({ length: rows }).map((_, i) => (
          <CalendarSkeleton key={i} opacity={pulseAnim} />
        ))}
      </View>
    );
  }

  // card variant
  return (
    <View style={styles.container}>
      {Array.from({ length: rows }).map((_, i) => (
        <View key={i} style={styles.cardSkeleton}>
          <SkeletonBar width="60%" height={14} opacity={pulseAnim} />
          <SkeletonBar width="90%" height={10} opacity={pulseAnim} />
          <SkeletonBar width="40%" height={10} opacity={pulseAnim} />
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingVertical: SPACING.sm,
  },
  bar: {
    marginBottom: SPACING.sm,
  },
  sectionHeader: {
    paddingHorizontal: SPACING.lg,
    paddingVertical: SPACING.md,
  },
  // Reminder skeleton
  reminderCard: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: RADIUS.xl,
    padding: SPACING.base,
    marginHorizontal: SPACING.base,
    marginVertical: 5,
    borderWidth: 0.5,
    gap: SPACING.md,
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  reminderContent: {
    flex: 1,
    gap: SPACING.sm,
  },
  reminderMeta: {
    flexDirection: 'row',
    gap: SPACING.md,
  },
  actionCircle: {
    width: 38,
    height: 38,
    borderRadius: 19,
  },
  // Chat skeleton
  chatRow: {
    flexDirection: 'row',
    paddingHorizontal: SPACING.base,
    marginBottom: SPACING.sm,
  },
  chatRowUser: {
    justifyContent: 'flex-end',
  },
  chatBubble: {
    borderRadius: RADIUS.lg,
    padding: SPACING.md,
    gap: 6,
    maxWidth: '70%',
  },
  chatBubbleUser: {
    borderRadius: RADIUS.lg,
  },
  // Calendar skeleton
  calendarItem: {
    paddingHorizontal: SPACING.base,
    paddingVertical: SPACING.md,
    marginHorizontal: SPACING.base,
    marginBottom: 6,
    borderLeftWidth: 3,
    gap: 6,
  },
  // Card skeleton
  cardSkeleton: {
    padding: SPACING.base,
    marginHorizontal: SPACING.base,
    marginBottom: SPACING.md,
    gap: SPACING.md,
  },
});
